'use client'

import {
  useAuthenticatedPerson,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import { getFullPersonQuery } from '@b-stock/fe-core/bstock-next/queries'

/**
 * Whether the id Braze identifies the user under is final for this page load.
 *
 * fe-core's Braze component only calls changeUser() once it knows the
 * visitor's authenticated person id -- a still-loading or logged-out visitor
 * is left unidentified (Braze's own anonymous profile), not assigned a
 * placeholder id. Braze does not re-attribute events that were already
 * logged once a later changeUser() call fires, so anything logged while the
 * person request is still in flight would be attributed to the wrong
 * identity once it resolves.
 */
export default function useBrazeIdentityResolved(): boolean {
  const personId = useDecodedAccessToken()?.personId
  const { isPending } = getFullPersonQuery.useQuery(personId)
  const person = useAuthenticatedPerson()

  // No access token means there is no person request to wait on, so the
  // anonymous id is already the final one.
  if (!personId) return true
  if (person) return true

  // useAuthenticatedPerson also returns null once the request has settled without
  // a usable record. Waiting on `person` alone would never release those users,
  // dropping the event entirely instead of falling back to the anonymous id.
  return !isPending
}
